import axios, { AxiosResponse } from 'axios';
import { ICountry } from '../models/ICountry';
import { ICard } from '../models/ICard';
import { IUser } from '../models/IUser';
import { IVisaCard } from '../models/IVisaCard';

const BASE_URL = 'https://api.immigrate.site/v2';

/**
 * Fetches all cards from the API.
 * @returns {Promise<any>} The response data containing cards.
 */
export const getCards = async () => {
	const response = await axios.get(`${BASE_URL}/getCards`);
	return response.data as ICard[];
};

/**
 * Gets the next card, countries, or user info based on the provided payload.
 * @param {Object} payload - The request payload.
 * @param {string} [payload.sessionId] - The session identifier.
 * @param {number} [payload.cardId] - The card identifier.
 * @param {number} [payload.value] - The value/answer for the card.
 * @returns {Promise<{ countries: ICountry[], card: ICard | null, user: IUser | null }>} The result object.
 */
export const getNextCard = async (payload: {
	sessionId?: string;
	cardId?: number;
	value?: number;
}) => {
	const { cardId, sessionId, value } = payload;
	const data: { session_id?: string; answer?: { card_id: number; value: number; } } = {};
	if (sessionId) data.session_id = sessionId;
	if (cardId) data.answer = { card_id: cardId, value: value || 0 };
	const response = await axios.post(`${BASE_URL}/getNextCard`, data);
	const resData = response.data as Array<{ countries: ICountry[] } | { cards: ICard[] } | { user: IUser[]}>;

	const res = resData.reduce((acc: { countries: ICountry[], card: ICard | null, user: IUser | null }, item) => {
    if ('countries' in item)
        acc.countries = item.countries || [];
    if ('cards' in item)
        acc.card = item?.cards[0] || null;
    if ('user' in item)
        acc.user = item?.user[0] || null;
    return acc
	}, { countries: [], card: null, user: null });
	return res
};

/**
 * Fetches the results for a given session.
 * @param {Object} payload - The request payload.
 * @param {string} payload.sessionId - The session identifier.
 * @returns {Promise<{ data?: ICountry[] }>} The response data containing countries.
 */
export const getResults = async (payload: {
	sessionId: string;
}) => {
	const { sessionId } = payload;
	const response = await axios.get(`${BASE_URL}/getResult?token=${sessionId}`);
	const resData = response as { data?: ICountry[] };
	return resData;
};

export const answerVisaQuestion = async (payload: {
	sessionId: string;
	cardId: number;
	value: string;
}) => {
	const { cardId, sessionId, value } = payload;
	const data: { session_id?: string; answer?: { q_id: number; value: string; } } = {};
	if (sessionId) data.session_id = sessionId;
	if (cardId) data.answer = { q_id: cardId, value: value || '' };
	await axios.post(`${BASE_URL}/getNextVisaApplicabilityQuestions`, data);
};

export const getVisaApplicabilityQuestions = async () => {
	const res = await axios.get(`${BASE_URL}/getVisaApplicabilityQuestions`);
	return res.data as IVisaCard[];
};

export const getVisaResultLongPolling = async (payload: {
	sessionId: string;
}) => {
	const { sessionId } = payload;
	let attempts = 0;
	
	while(true) {
		attempts++;
		if (attempts > 2000)
			throw new Error(`Reached maximum of attempts`)
		const res = await axios.get(`${BASE_URL}/getVisaResult?token=${sessionId}`)
		if (res.status < 200 || res.status > 299)
			throw new Error('Request status is ' + res.status);
		if (res?.data?.countries)
			return  res?.data?.countries;
		const status = res?.data?.response?.status;
		if (!status)
			throw new Error('Incorrect processing status');
		if (status === 'invalid_token' || status === 'profile_missing')
			throw new Error(`Returned status ${status}`);
		if (status === 'in_progress') {
			await new Promise(r => setTimeout(r, 1500));
			continue;
		}
		
	}
};
